// Document upload configuration and validation utilities

// Supported file types for document analysis
export const SUPPORTED_FILE_TYPES = {
  'pdf': {
    mimeType: 'application/pdf',
    extension: '.pdf',
    description: 'PDF documents',
    icon: '📄',
    maxSize: 10 * 1024 * 1024, // 10MB
    analysisSupported: true
  },
  'txt': {
    mimeType: 'text/plain',
    extension: '.txt',
    description: 'Text files',
    icon: '📝',
    maxSize: 10 * 1024 * 1024, // 10MB
    analysisSupported: true
  },
  'md': {
    mimeType: 'text/markdown',
    extension: '.md',
    description: 'Markdown files',
    icon: '📋',
    maxSize: 10 * 1024 * 1024, // 10MB
    analysisSupported: true
  }
};

// Additional file types that can be uploaded but not analyzed
export const ADDITIONAL_FILE_TYPES = {
  'doc': {
    mimeType: 'application/msword',
    extension: '.doc',
    description: 'Word documents (legacy)',
    icon: '📘',
    maxSize: 10 * 1024 * 1024,
    analysisSupported: false
  },
  'docx': {
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extension: '.docx',
    description: 'Word documents',
    icon: '📘',
    maxSize: 10 * 1024 * 1024,
    analysisSupported: false
  },
  'jpg': {
    mimeType: 'image/jpeg',
    extension: '.jpg',
    description: 'JPEG images',
    icon: '🖼️',
    maxSize: 10 * 1024 * 1024,
    analysisSupported: false
  },
  'jpeg': {
    mimeType: 'image/jpeg',
    extension: '.jpeg',
    description: 'JPEG images',
    icon: '🖼️',
    maxSize: 10 * 1024 * 1024,
    analysisSupported: false
  },
  'png': {
    mimeType: 'image/png',
    extension: '.png',
    description: 'PNG images',
    icon: '🖼️',
    maxSize: 10 * 1024 * 1024,
    analysisSupported: false
  }
};

// Default upload preferences
export const DEFAULT_UPLOAD_PREFERENCES = {
  restrictToAnalyzableFiles: true,
  allowAdditionalFileTypes: false,
  showAnalysisWarning: true,
  autoSelectUploadedDocuments: true,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxFilesPerUpload: 5
};

// File validation function
export const validateFile = (file, preferences = DEFAULT_UPLOAD_PREFERENCES) => {
  const errors = [];
  
  // Check file size
  if (file.size > preferences.maxFileSize) {
    errors.push(`File "${file.name}" exceeds the maximum size limit of ${(preferences.maxFileSize / 1024 / 1024).toFixed(0)}MB`);
  }
  
  // Get file extension
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  
  // Check if file type is supported for analysis
  const isSupportedForAnalysis = Object.keys(SUPPORTED_FILE_TYPES).includes(fileExtension);
  const isAdditionalType = Object.keys(ADDITIONAL_FILE_TYPES).includes(fileExtension);
  
  if (preferences.restrictToAnalyzableFiles) {
    // Only allow files that can be analyzed
    if (!isSupportedForAnalysis) {
      const supportedTypes = Object.values(SUPPORTED_FILE_TYPES).map(type => type.extension).join(', ');
      errors.push(`File "${file.name}" cannot be analyzed. Only ${supportedTypes} files are supported for AI analysis.`);
    }
  } else {
    // Allow additional file types if preference is set
    if (!isSupportedForAnalysis && !isAdditionalType) {
      const allTypes = [
        ...Object.values(SUPPORTED_FILE_TYPES),
        ...(preferences.allowAdditionalFileTypes ? Object.values(ADDITIONAL_FILE_TYPES) : [])
      ].map(type => type.extension).join(', ');
      errors.push(`File "${file.name}" is not a supported file type. Supported types: ${allTypes}`);
    }
  }
  
  // Check MIME type as additional validation
  if (errors.length === 0) {
    const fileInfo = SUPPORTED_FILE_TYPES[fileExtension] || ADDITIONAL_FILE_TYPES[fileExtension];
    if (fileInfo && file.type && file.type !== fileInfo.mimeType) {
      // Only warn, don't block upload (some browsers handle MIME types differently)
      console.warn(`File "${file.name}" has unexpected MIME type: ${file.type}, expected: ${fileInfo.mimeType}`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    canBeAnalyzed: isSupportedForAnalysis,
    fileInfo: SUPPORTED_FILE_TYPES[fileExtension] || ADDITIONAL_FILE_TYPES[fileExtension]
  };
};

// Get accepted file types string for input elements
export const getAcceptedFileTypes = (preferences = DEFAULT_UPLOAD_PREFERENCES) => {
  let types = Object.values(SUPPORTED_FILE_TYPES).map(type => type.extension);
  
  if (!preferences.restrictToAnalyzableFiles && preferences.allowAdditionalFileTypes) {
    types = [...types, ...Object.values(ADDITIONAL_FILE_TYPES).map(type => type.extension)];
  }
  
  return types.join(',');
};

// Alias for consistency with existing imports
export const getFileTypeAcceptString = getAcceptedFileTypes;

// Get file type description
export const getFileTypeDescription = (preferences = DEFAULT_UPLOAD_PREFERENCES) => {
  if (preferences.restrictToAnalyzableFiles) {
    return "PDF, TXT, MD files (supported for AI analysis)";
  }
  
  let description = "PDF, TXT, MD files (AI analysis supported)";
  if (preferences.allowAdditionalFileTypes) {
    description += ", DOC, DOCX, JPG, PNG (storage only)";
  }
  
  return description;
};

// Generate unique file ID for preventing duplicate keys
export const generateFileId = (file, timestamp = Date.now()) => {
  return `file-${timestamp}-${file.name.replace(/[^a-zA-Z0-9]/g, '')}-${file.size}`;
};
